// Generic subscribable value with subscribe / setValue / update +
// listener fan-out. Per OC §3.3 (refactor review) — extracted at the
// 3rd consumer (Store D-082 + persistence-error D-083 + toast queue
// P-035). The 3-use threshold was flagged in the D-083 forward note;
// P-035 adds toast + lastSavedAt + pendingWrites streams which all
// share the same shape.
//
// **Why hand-rolled (not a library).** Per OC §3.8: a library
// (signals, mobx, rxjs) would add a learning surface + dep + upgrade
// obligation for ~30 LOC of pure logic. The factory has no
// dependencies and ships as a thin typed seam over a Set + closure.
//
// **Subscribe semantics** mirror the Store contract (D-082 / Tech Arch
// §11.7): synchronous fan-out over the listener Set in insertion
// order; native Set iterator handles listener self-unsubscribe-during-
// notification correctly (already-visited listeners aren't revisited;
// later listeners still fire).
//
// **No-op detection.** `setValue(next)` and `update(updater)` skip the
// fan-out when the next value is referentially equal to the current.
// Updaters that return the same object intentionally signal "no
// change"; firing listeners would cause spurious downstream re-renders.

export interface Subscribable<T> {
  getValue(): T
  setValue(next: T): void
  update(updater: (prev: T) => T): void
  subscribe(listener: (value: T) => void): () => void
}

/**
 * Build a `Subscribable<T>` seeded with `initialValue`. All consumers
 * (Store, persistence-error, toast queue, lastSavedAt, pendingWrites)
 * share this shape.
 */
export function createSubscribable<T>(initialValue: T): Subscribable<T> {
  let value = initialValue
  const listeners = new Set<(value: T) => void>()

  function notify(): void {
    for (const listener of listeners) listener(value)
  }

  return {
    getValue: () => value,
    setValue: (next) => {
      if (next === value) return
      value = next
      notify()
    },
    update: (updater) => {
      const next = updater(value)
      if (next === value) return
      value = next
      notify()
    },
    subscribe: (listener) => {
      listeners.add(listener)
      return () => {
        listeners.delete(listener)
      }
    },
  }
}
